import { NgTemplateOutlet } from '@angular/common';
import type { Signal } from '@angular/core';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { Observable } from 'rxjs';

import type { BadgeItemType, IconAvatar, Maybe, MenuPosition, ValueOf } from '@evc/web-components';
import {
  MenuComponent,
  MenuItemComponent,
  MenuTriggerStateDirective,
  WebComponentsConfigService,
} from '@evc/web-components';

import { AuthService } from '../../../../clients/core/auth/service/auth.service';
import { OrganizationsService } from '../../../../clients/core/organizations/organizations.service';
import { Organization } from '../../../../clients/core/organizations/organizations.type';
import { UserService } from '../../../../clients/core/user/user.service';
import { CoreClientUtilService } from '../../../../clients/core/utils/core-client-utils.service';
import { PlatformConfigService } from '../../../config/config.service';
import { I18nPlatformService } from '../../../i18n/i18n.service';
import { PlatformTranslatePipe } from '../../../i18n/platform-translate.pipe';
import { Guid } from '../../../types/api-objects.type';
import {
  NAVIGATIONS_HOSTS,
  NAVIGATIONS_MODES,
  NavigationsHost,
  NavigationsMode,
} from './../../types';
import { UserBadgeComponent } from './user-badge';

export const PERIODS_OF_DAY = {
  ['3']: 'morning',
  ['12']: 'afternoon',
  ['17']: 'evening',
} as const;
export type PeriodOfDay = ValueOf<typeof PERIODS_OF_DAY>;

@Component({
  selector: 'evc-user-menu',
  imports: [
    MenuItemComponent,
    PlatformTranslatePipe,
    MenuComponent,
    MenuTriggerStateDirective,
    UserBadgeComponent,
    NgTemplateOutlet,
  ],
  templateUrl: './user-menu.html',
  styleUrls: ['./user-menu.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[attr.data-only-menu]': 'onlyMenu()',
    '[attr.data-pending]': 'isPending()',
  },
})
export class UserMenuComponent {
  readonly #authService = inject(AuthService);
  readonly #coreClientUtilService = inject(CoreClientUtilService);
  readonly #organizationsService = inject(OrganizationsService);
  readonly #i18nPlatformService = inject(I18nPlatformService);
  readonly #platformConfigService = inject(PlatformConfigService);
  readonly #webComponentsConfigService = inject(WebComponentsConfigService);

  readonly #userService = inject(UserService);
  userProfile = this.#userService.profile.bind(this.#userService);

  protected readonly isPending = this.#authService.shouldPromptOrganizationSelection;

  selected = input<boolean>(false);
  layout = input<NavigationsMode>(
    this.userProfile() ? NAVIGATIONS_MODES.PRIVATE : NAVIGATIONS_MODES.PUBLIC,
  );
  host = input<NavigationsHost>(NAVIGATIONS_HOSTS.TOPBAR);
  isIms = input(false);
  onlyMenu = input<boolean>(false);

  readonly position = input<MenuPosition>();
  readonly menuPosition = computed(() => {
    const fromProps = this.position();
    const inBurger = this.host() === NAVIGATIONS_HOSTS.BURGER;

    const position = fromProps ?? (inBurger ? 'top-center' : 'bottom-right');

    return position;
  });

  // readonly menu = viewChild<MenuComponent>('menu');

  MAX_ORGANIZATIONS_BEFORE_SCROLL = 3;
  protected readonly layouts = NAVIGATIONS_MODES;

  organizations: Signal<BadgeItemType[]>;
  showOrganizations = computed(
    () => this.#platformConfigService.useConnect() || this.organizations().length > 0,
  );
  greetings: Signal<string>;

  menuItems = computed<Record<'userAccount' | 'addOrganization' | 'logout', BadgeItemType>>(() => {
    const avatar: Omit<IconAvatar, 'icon'> = {
      type: 'icon',
      color: 'var(--content-action-link)',
    };

    return {
      userAccount: {
        id: 'user-account',
        name: this.#i18nPlatformService.tSignal('menus.user.account')(),
        avatar: { ...avatar, icon: 'profile' },
      },
      addOrganization: {
        id: 'add-organization',
        name: this.#i18nPlatformService.tSignal('menus.user.organizations.add')(),
        avatar: { ...avatar, icon: 'add', bg_color: 'var(--fill-accent-subtle)' },
      },
      logout: {
        id: 'logout',
        name: this.#i18nPlatformService.tSignal('menus.user.logout')(),
        avatar: { ...avatar, icon: 'log-out' },
      },
    };
  });

  constructor() {
    this.organizations = computed(() =>
      (this.#organizationsService.entries() as Organization[]).map((organization) =>
        this.#coreClientUtilService.formatToBadgeItemType(organization),
      ),
    );

    this.greetings = this.#i18nPlatformService.tSignal(
      `menus.user.greetings.${this.#getPeriodOfDay()}`,
    );
  }

  readonly login = input<Maybe<() => void>>(undefined);
  onLogin(): void {
    const loginFn = this.login();
    if (typeof loginFn === 'function') {
      loginFn();

      return;
    }

    if (!this.#platformConfigService.useConnect()) return;

    this.#authService.login({ redirectStartPage: window.location.href }).subscribe();
  }

  readonly logout = input<Maybe<() => void>>(undefined);
  onLogout(): void | Observable<void> {
    const logoutFn = this.logout();
    if (typeof logoutFn === 'function') {
      return logoutFn();
    }

    return this.#platformConfigService.useConnect() ? this.#authService.logout() : undefined;
  }

  readonly changeOrganization = input<Maybe<(organizationId: Guid) => void>>(undefined);
  onChangeOrganization(organizationId: Guid): void | Observable<void> {
    const changeOrganizationFn = this.changeOrganization();
    if (typeof changeOrganizationFn === 'function') {
      return changeOrganizationFn(organizationId);
    }

    return this.#organizationsService.setCurrent(organizationId, true);
  }

  redirectUserProfile(): void {
    const { user } = this.#platformConfigService.get('uris');
    this.#webComponentsConfigService.openLink(user);
  }

  addOrganization(): void {
    this.#authService.redirectToCreateOrganization(document.location.href);
  }

  #getPeriodOfDay(): PeriodOfDay {
    const nowH = new Date().getHours();
    const periodKey =
      (Object.keys(PERIODS_OF_DAY)
        .reverse()
        .find((key) => nowH >= parseInt(key, 10)) as keyof typeof PERIODS_OF_DAY) || '17';

    return PERIODS_OF_DAY[periodKey];
  }
}
